'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import type { ButtonVariant, ButtonSize } from './Button';

export interface LinkButtonProps {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  target?: string;
  rel?: string;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-xs min-h-[36px] rounded-full',
  md: 'px-6 py-3 text-sm min-h-[44px] rounded-full',
  lg: 'px-8 py-4 text-base min-h-[52px] rounded-full',
};

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: { background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' },
  secondary: { background: 'transparent', color: '#6FD3E0', border: '1.5px solid #6FD3E0' },
  outline: { background: 'transparent', color: '#6FD3E0', border: '1.5px solid #6FD3E0' },
  ghost: { background: 'transparent', color: '#C8DDE6', border: '1.5px solid transparent' },
};

const variantHoverStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: { background: '#5ec4d1', color: '#0B1622', border: '1.5px solid #5ec4d1' },
  secondary: { background: 'rgba(111,211,224,0.10)', color: '#6FD3E0', border: '1.5px solid #6FD3E0' },
  outline: { background: 'rgba(111,211,224,0.10)', color: '#6FD3E0', border: '1.5px solid rgba(111,211,224,0.75)' },
  ghost: { background: 'rgba(111,211,224,0.06)', color: '#EAF4F8', border: '1.5px solid transparent' },
};

const LinkButton = ({
  href,
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  style,
  children,
  target,
  rel,
}: LinkButtonProps) => {
  const [hovered, setHovered] = React.useState(false);

  const baseStyle: React.CSSProperties = {
    ...(hovered ? variantHoverStyles[variant] : variantStyles[variant]),
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 600,
    letterSpacing: '0.01em',
    textDecoration: 'none',
    width: fullWidth ? '100%' : undefined,
    ...style,
  };

  return (
    <Link
      href={href}
      style={baseStyle}
      className={`${sizeClasses[size]} ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      target={target}
      rel={rel}
    >
      {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
      <span>{children}</span>
      {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </Link>
  );
};

export default LinkButton;
